import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useUI } from '@/lib/store';

/**
 * Small paper card listing the four keyboard shortcuts. Asymmetric layout:
 *
 *   [ kbd ]   action in italic
 *
 * The kbd glyphs are monospace inside small inset cards (the only place we
 * use monospace; numbers in the rest of the product are tabular Outfit).
 *
 * Triggered by `?` and dismissed by `?` again, Esc, or click-outside.
 * The overlay tilts -1.2°, sits above-right of center, and fades in over
 * 240ms. No perpetual motion — this is documentation, not a dashboard.
 */
const SHORTCUTS: Array<{ key: string; label: string }> = [
  { key: 'N',   label: 'a new place' },
  { key: 'M',   label: 'leave a note' },
  { key: 'esc', label: 'close anything' },
  { key: '?',   label: 'this little card' },
];

export function HelpOverlay() {
  const open = useUI((s) => s.helpOpen);
  const close = useUI((s) => s.closeHelp);
  const reduced = useReducedMotion();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="help"
          onClick={close}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
          role="dialog"
          aria-label="keyboard shortcuts"
          aria-modal="true"
        >
          <motion.article
            className="help__card u-grain"
            onClick={(e) => e.stopPropagation()}
            initial={reduced
              ? { opacity: 0, rotate: -1.2 }
              : { opacity: 0, y: 16, scale: 0.96, rotate: -1.2 }}
            animate={reduced
              ? { opacity: 1, rotate: -1.2 }
              : { opacity: 1, y: 0, scale: 1, rotate: -1.2 }}
            exit={reduced
              ? { opacity: 0, rotate: -1.2 }
              : { opacity: 0, y: 12, scale: 0.96, rotate: -1.2 }}
            transition={{ type: 'spring', stiffness: 180, damping: 22 }}
          >
            <header className="help__head">
              <h2 className="help__title">a few keystrokes</h2>
              <p className="help__sub">tap or press <kbd>?</kbd> to dismiss</p>
            </header>

            <ol className="help__list">
              {SHORTCUTS.map((s, i) => (
                <motion.li
                  key={s.key}
                  initial={reduced ? { opacity: 1 } : { opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: reduced ? 0 : 0.08 + i * 0.04, duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                  className="help__item"
                >
                  <kbd className="help__key">{s.key}</kbd>
                  <span className="help__label">{s.label}</span>
                </motion.li>
              ))}
            </ol>

            <footer className="help__foot">for two.</footer>
          </motion.article>

          <style>{css}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const css = `
.help {
  position: fixed; inset: 0;
  z-index: 60;
  background: rgba(92, 75, 87, 0.22);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: grid; place-items: center;
  padding: var(--space-5);
  cursor: zoom-out;
}
.help__card {
  position: relative;
  cursor: auto;
  width: min(360px, calc(100vw - 32px));
  background:
    linear-gradient(180deg, var(--c-paper) 0%, var(--c-paper-aged) 100%);
  border: 1px solid var(--c-border-soft);
  border-radius: 6px 22px 6px 22px;
  padding: var(--space-5) var(--space-5) var(--space-4);
  /* Tinted shadow, not blue */
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.5),
    0 24px 48px -20px rgba(196,150,164,0.35);
}
.help__head {
  display: grid; gap: 4px;
  margin-bottom: var(--space-4);
  padding-bottom: var(--space-3);
  border-bottom: 1px dashed var(--c-ink-whisper);
}
.help__title {
  font-family: var(--font-display);
  font-style: italic;
  font-size: 1.45rem;
  font-weight: 400;
  color: var(--c-ink);
  letter-spacing: -0.005em;
  margin: 0;
}
.help__sub {
  font-family: var(--font-hand);
  font-size: 0.95rem;
  color: var(--c-ink-soft);
  margin: 0;
}
.help__sub kbd {
  font-family: ui-monospace, 'JetBrains Mono', SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
  background: var(--c-cream);
  padding: 1px 5px;
  border-radius: 4px;
  border: 1px solid var(--c-border-soft);
  vertical-align: 1px;
}

.help__list {
  list-style: none; padding: 0; margin: 0;
  display: grid;
  grid-template-columns: 56px 1fr;
  gap: 14px 16px;
  align-items: center;
}
.help__item {
  display: contents;
}
.help__key {
  /* Inset paper "key" — monospace inside a soft tile, tinted edge */
  display: inline-grid; place-items: center;
  min-width: 44px; height: 32px;
  padding: 0 8px;
  background: var(--c-cream);
  color: var(--c-rose-deep);
  font-family: ui-monospace, 'JetBrains Mono', SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  letter-spacing: 0.03em;
  border-radius: 6px;
  border: 1px solid var(--c-border);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.6),
    inset 0 -1px 0 rgba(196,150,164,0.10),
    0 2px 4px -2px rgba(196,150,164,0.20);
  text-transform: uppercase;
}
.help__label {
  font-family: var(--font-display);
  font-style: italic;
  font-size: 1.05rem;
  color: var(--c-ink);
  letter-spacing: 0.005em;
}
.help__foot {
  margin-top: var(--space-5);
  font-family: var(--font-hand);
  font-size: 1rem;
  color: var(--c-ink-faint);
  text-align: right;
}
`;
